/**
 * Geometry for the reservation calendar (#642): which part of a day a
 * booking, a closure or the closed hours cover, in minutes since local
 * midnight, and the time span a grid has to show.
 *
 * Everything is by the strings' local components (see reservationMeta);
 * a calendar drawn through `Date` in the viewer's zone would put a
 * Saturday 2 pm booking at 11 am for a manager in California.
 *
 * Pure, so it is unit-tested.
 */
import {
  AmenityClosureReturn,
  AmenityHoursWindow,
  AmenityReturn,
  AmenitySeasonReturn,
  ReservationReturn,
} from '../clients/rust-api-client';
import {
  addDays,
  dayHours,
  localDay,
  localMinute,
  nightsBetween,
  weekdayOf,
  windowsFor,
} from './reservationMeta';

export interface Span {
  start: number; // minutes since local midnight, inclusive
  end: number; // exclusive, up to 1440
}

/**
 * The part of [start, end) that falls on `day`, or null. A booking that
 * runs past midnight shows on both days, each clipped to its own day.
 */
export const spanOnDay = (startLocal: string, endLocal: string, day: string): Span | null => {
  const sDay = localDay(startLocal);
  const eDay = localDay(endLocal);
  const sMin = localMinute(startLocal);
  const eMin = localMinute(endLocal);
  if (!sDay || !eDay || sMin === null || eMin === null) return null;
  if (sDay > day || eDay < day) return null;
  const start = sDay < day ? 0 : sMin;
  const end = eDay > day ? 1440 : eMin;
  return end > start ? { start, end } : null;
};

export const reservationSpan = (r: ReservationReturn, day: string): Span | null =>
  spanOnDay(r.start_local, r.end_local, day);

export const closureSpan = (c: AmenityClosureReturn, day: string): Span | null =>
  spanOnDay(c.starts_local, c.ends_local, day);

/** The hours of `day` the amenity is shut, as spans (the complement of its windows). */
export const closedSpans = (hours: AmenityHoursWindow[], day: string): Span[] => {
  const windows = windowsFor(hours, weekdayOf(day));
  const out: Span[] = [];
  let cursor = 0;
  for (const w of windows) {
    if (w.open_minute > cursor) out.push({ start: cursor, end: w.open_minute });
    cursor = Math.max(cursor, w.close_minute);
  }
  if (cursor < 1440) out.push({ start: cursor, end: 1440 });
  return out;
};

/**
 * The hours of `day` an amenity is shut, seasons included (P2): the
 * complement of the day's windows, the whole day when it is out of
 * season, and nothing when no hours are set at all — the server treats an
 * amenity without hours as open round the clock, so the grid does too.
 */
export const closedSpansFor = (
  amenity: { hours: AmenityHoursWindow[]; seasons?: AmenitySeasonReturn[] },
  day: string
): Span[] => {
  const h = dayHours(amenity.hours, amenity.seasons ?? [], day);
  if (h.kind === 'unconfigured') return [];
  if (h.kind === 'out_of_season') return [{ start: 0, end: 1440 }];
  const out: Span[] = [];
  let cursor = 0;
  for (const w of h.windows) {
    if (w.open_minute > cursor) out.push({ start: cursor, end: w.open_minute });
    cursor = Math.max(cursor, w.close_minute);
  }
  if (cursor < 1440) out.push({ start: cursor, end: 1440 });
  return out;
};

/** The windows an amenity is open on `day`, seasons included (empty when shut or unconfigured). */
export const openWindowsFor = (
  amenity: { hours: AmenityHoursWindow[]; seasons?: AmenitySeasonReturn[] },
  day: string
): AmenityHoursWindow[] => {
  const h = dayHours(amenity.hours, amenity.seasons ?? [], day);
  return h.kind === 'windows' ? h.windows : [];
};

/**
 * The window of the day the grid shows: from the earliest opening to the
 * latest closing across what is on screen, widened to cover any booking
 * outside hours (a manager override), on whole hours. 8 AM – 8 PM when
 * nothing says otherwise.
 */
export const visibleRange = (
  hoursList: AmenityHoursWindow[][],
  days: string[],
  bookings: Span[]
): Span =>
  visibleRangeOf(
    hoursList.flatMap((hours) => days.map((day) => windowsFor(hours, weekdayOf(day)))),
    bookings
  );

/** `visibleRange` over windows already resolved per column (seasons applied). */
export const visibleRangeOf = (windowSets: AmenityHoursWindow[][], bookings: Span[]): Span => {
  let start = Infinity;
  let end = -Infinity;
  for (const windows of windowSets) {
    for (const w of windows) {
      start = Math.min(start, w.open_minute);
      end = Math.max(end, w.close_minute);
    }
  }
  for (const b of bookings) {
    start = Math.min(start, b.start);
    end = Math.max(end, b.end);
  }
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return { start: 8 * 60, end: 20 * 60 };
  }
  return {
    start: Math.floor(start / 60) * 60,
    end: Math.min(1440, Math.ceil(end / 60) * 60),
  };
};

/** The seven days of the week containing `day`, starting on `weekStartsOn` (0 = Sunday). */
export const weekDays = (day: string, weekStartsOn = 0): string[] => {
  const back = (weekdayOf(day) - weekStartsOn + 7) % 7;
  const first = addDays(day, -back);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
};

/** The minute a click lands on, snapped down to the amenity's slot increment. */
export const snapMinute = (minute: number, increment: number): number => {
  const step = increment > 0 ? increment : 30;
  return Math.max(0, Math.min(1440 - step, Math.floor(minute / step) * step));
};

// ---------------------------------------------------------------------------
// Shared capacity (P2): several households in one slot
// ---------------------------------------------------------------------------

export interface Laned<T> {
  item: T;
  span: Span;
  /** 0-based column inside its cluster of overlapping bookings. */
  lane: number;
  /** How many lanes its cluster needs, so each can take 1/lanes of the width. */
  lanes: number;
}

/**
 * Side-by-side lanes for bookings that overlap — on a shared-capacity
 * amenity several households hold the same hour, and stacking them would
 * hide all but one. Greedy interval colouring, per cluster of mutually
 * overlapping spans, so a quiet afternoon keeps full-width blocks.
 */
export const assignLanes = <T>(items: { item: T; span: Span }[]): Laned<T>[] => {
  const sorted = [...items].sort((a, b) => a.span.start - b.span.start || b.span.end - a.span.end);
  const out: Laned<T>[] = [];
  let cluster: Laned<T>[] = [];
  let clusterEnd = -Infinity;
  let laneEnds: number[] = [];
  const flush = () => {
    const lanes = Math.max(1, laneEnds.length);
    cluster.forEach((c) => out.push({ ...c, lanes }));
    cluster = [];
    laneEnds = [];
  };
  for (const it of sorted) {
    if (it.span.start >= clusterEnd) {
      flush();
      clusterEnd = -Infinity;
    }
    let lane = laneEnds.findIndex((end) => end <= it.span.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.span.end);
    } else {
      laneEnds[lane] = it.span.end;
    }
    cluster.push({ ...it, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, it.span.end);
  }
  flush();
  return out;
};

export interface Occupancy extends Span {
  /** Headcount booked in this stretch: the largest at any moment of it. */
  used: number;
}

/**
 * How full a shared-capacity amenity is, in `step`-minute stretches of
 * `range`: the peak headcount (sum of party sizes of overlapping
 * bookings) inside each stretch. Stretches nobody booked are left out.
 */
export const occupancy = (
  bookings: { span: Span; party: number }[],
  range: Span,
  step = 60
): Occupancy[] => {
  const out: Occupancy[] = [];
  for (let m = range.start; m < range.end; m += step) {
    const end = Math.min(range.end, m + step);
    // The peak inside [m, end) is reached at one of the start points in it.
    const points = [m, ...bookings.map((b) => b.span.start).filter((p) => p > m && p < end)];
    let used = 0;
    for (const p of points) {
      const atP = bookings
        .filter((b) => b.span.start <= p && b.span.end > p)
        .reduce((sum, b) => sum + b.party, 0);
      used = Math.max(used, atP);
    }
    if (used > 0) out.push({ start: m, end, used });
  }
  return out;
};

// ---------------------------------------------------------------------------
// Stays (P2): an overnight booking across several days
// ---------------------------------------------------------------------------

/**
 * What a stay is doing on `day`: arriving, a night in the middle, or
 * leaving — with the night's number — for the label on its block. Null
 * when the stay is not on that day.
 */
export const stayOnDay = (
  startLocal: string,
  endLocal: string,
  day: string
): { kind: 'check_in' | 'night' | 'check_out'; night: number; nights: number } | null => {
  const sDay = localDay(startLocal);
  const eDay = localDay(endLocal);
  const nights = nightsBetween(startLocal, endLocal);
  if (!sDay || !eDay || nights === null || day < sDay || day > eDay) return null;
  const night = (nightsBetween(`${sDay}T00:00`, `${day}T00:00`) ?? 0) + 1;
  if (day === sDay) return { kind: 'check_in', night: 1, nights };
  if (day === eDay) return { kind: 'check_out', night: nights, nights };
  return { kind: 'night', night, nights };
};

/**
 * An amenity's closures for the calendar: the ones loaded for the visible
 * range (`GET /amenities/{id}/closures?from&to`, which includes past
 * closures) when they have arrived, else whatever the amenity carried —
 * `upcoming_closures`, which is empty on list reads and never past.
 */
export const closuresForAmenity = (
  amenity: Pick<AmenityReturn, 'id' | 'upcoming_closures'>,
  loaded: Record<string, AmenityClosureReturn[]>
): AmenityClosureReturn[] => loaded[amenity.id] ?? amenity.upcoming_closures ?? [];
